import { decodeStream, peekIsChunked } from 'services/attachmentV2/downloadManager'

// 신포맷 파싱이 이 코드로 실패하면 구포맷으로 강등한다 — "magic 만 우연히 일치한 구포맷" 방어.
// `Attachment/helper.js` 의 CHUNK_PARSE_FALLBACK_CODES 와 **같은 집합**이다(형제 관계).
// 공용 상수로 합치지 않은 이유: downloadManager 로 옮기면 그 모듈을 mock 하는 기존 테스트
// 5개가 상수를 제공하지 않아 `undefined.has(...)` 로 깨진다(mock 의 새 API 누락 함정).
// SIZE_MISMATCH·TRUNCATED 를 넣지 않는 이유: 헤더가 유효한 신포맷 파일의 **실제 손상**이라
// 구포맷으로 재시도해도 실패하고, 폴백하면 사용자가 원인을 알 수 없게 된다.
const LEGACY_FALLBACK_ERROR_CODES = new Set(['NOT_CHUNKED', 'BAD_HEADER', 'BAD_SOURCE'])

// 공통 export 유틸리티 (RecordDetail에서 추출)
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * 신포맷(SBC1) 첨부를 스트리밍 복호해 base64 로 만든다.
 *
 * 왜 필요한가: 청크 업로드가 8MiB 초과 파일을 SBC1 로 저장하는데, 내보내기 제외 상한은 50MB 라
 * **8MiB~50MB 파일은 신포맷이면서 내보내기 대상**이다. 아래 구포맷 경로(decryptBytes)에 SBC1
 * 바이트를 넣으면 PKCS#7 패딩 검증에서 예외가 난다(pUtils.js:86-88) — 즉 그 파일의 내보내기가
 * "첨부파일 실패"로 보고되어 사용자가 복구할 방법이 없다. 포맷을 판별해 맞는 복호기를 쓴다.
 */
const decryptChunkedBlobToBase64 = async (blob, rsKeyObj) => {
  const decoded = await decodeStream(blob, rsKeyObj.rsKey, { type: blob.type })
  return blobToBase64(decoded)
}

export const decryptBlobAndToBase64 = async (blob, rsKeyObj) => {
  // 판별 실패는 구포맷으로 강등한다 — peekIsChunked 는 어떤 실패에서도 false 를 돌려주므로
  // (downloadManager 의 방침) 판별 자체가 새 실패 경로를 만들지 않는다.
  // 판별이 true 여도 파싱이 폴백 코드로 실패하면 구포맷으로 재시도한다 — 이 PR 이 없애려는
  // "복구 불가 실패"를 이 경로에서도 완전히 막기 위해 helper 의 decode 와 대칭을 맞춘다
  // (PR #1802 리뷰).
  if (await peekIsChunked(blob)) {
    try {
      return await decryptChunkedBlobToBase64(blob, rsKeyObj)
    } catch (e) {
      if (!LEGACY_FALLBACK_ERROR_CODES.has(e?.code)) throw e
      console.warn('SBC1 magic matched but header invalid, falling back to legacy format')
    }
  }

  const arrayBuffer = await blob.arrayBuffer()
  const bytes = new Uint8Array(arrayBuffer)

  let binaryStr = ''
  for (let i = 0; i < Math.ceil(bytes.length / 32768.0); i += 1) {
    binaryStr += String.fromCharCode.apply(
      null,
      bytes.slice(i * 32768, Math.min((i + 1) * 32768, bytes.length)),
    )
  }

  const decrypted = await rsKeyObj.decryptBytes(binaryStr)
  const decryptedBytes = new Uint8Array(decrypted.length)
  for (let i = 0; i < decrypted.length; i += 1) {
    decryptedBytes[i] = decrypted.charCodeAt(i)
  }

  const decryptedBlob = new Blob([decryptedBytes])
  return blobToBase64(decryptedBlob)
}

export const downloadFile = (content, filename, type = 'application/json') => {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
